// WebSocket signaling client. Speaks the shared Lumi protocol and exposes a
// tiny typed event emitter. Auto-replies to server PINGs. ASCII only.

import { SERVER_WS } from './config.js';
import { ClientMsg, ServerMsg, HEARTBEAT_INTERVAL_MS } from '@lumi/shared';

// A dead server does not always close the socket: a killed process, a dropped
// mobile network, or a sleeping proxy can leave it HALF-OPEN, where onclose
// never fires and the page believes it is still connected. The server pings on
// a known interval, so silence past a few missed pings means the connection is
// gone -- and a chat must never outlive the server's knowledge of it.
const SERVER_SILENCE_MS = HEARTBEAT_INTERVAL_MS * 3;

export class Signaling {
  constructor() {
    this.ws = null;
    this.handlers = new Map(); // type -> Set<fn>
    this.closed = false;
    this.watchdog = null;
    this.lastSeen = 0;
    // Bumped by every connect(). A socket carries the number it was opened
    // with, and anything it says while holding a stale one is discarded --
    // otherwise the old socket's onclose (which arrives AFTER its replacement
    // is already open, and after a failed attempt too) would announce a
    // disconnection that has already been recovered from, and the page would
    // tear down a working connection.
    this.generation = 0;
  }

  /** Treat prolonged server silence as a disconnect. */
  _armWatchdog(gen) {
    clearInterval(this.watchdog);
    this.lastSeen = Date.now();
    let lastTick = Date.now();
    this.watchdog = setInterval(() => {
      if (this.closed || gen !== this.generation) {
        clearInterval(this.watchdog);
        this.watchdog = null;
        return;
      }
      // A tick that itself arrives late is a page that was FROZEN -- a phone
      // locked, a tab left behind others -- not a server that went quiet. The
      // page heard nothing because it was not running, and closing a healthy
      // socket for that was a needless drop after every long background (an
      // Android at 19 minutes and again at 5, 2026-09-24). Start the clock
      // again from now: a server that is truly gone shows up as silence over
      // the next few ordinary ticks.
      const now = Date.now();
      const frozen = now - lastTick > SERVER_SILENCE_MS;
      lastTick = now;
      if (frozen) {
        this.lastSeen = now;
        return;
      }
      if (now - this.lastSeen < SERVER_SILENCE_MS) return;
      clearInterval(this.watchdog);
      this.watchdog = null;
      try {
        this.ws?.close();
      } catch {
        /* already gone */
      }
      this._emit('__close', { reason: 'silent' });
    }, 5000);
  }

  on(type, fn) {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type).add(fn);
    return () => this.handlers.get(type)?.delete(fn);
  }

  _emit(type, payload) {
    this.handlers.get(type)?.forEach((fn) => {
      try {
        fn(payload);
      } catch (err) {

        console.error('handler error', type, err);
      }
    });
  }

  /**
   * Open a socket. Safe to call again on the same instance to REPLACE a dead
   * one: the handlers registered with on() survive, so a reconnect does not
   * have to rewire the whole protocol. The token is not reusable -- it lives
   * two minutes -- so a caller reconnecting must mint a fresh one.
   */
  connect(token) {
    // The session token rides in Sec-WebSocket-Protocol, not the query
    // string. It used to be in the URL, and nginx logs the request line: every
    // connection wrote a replayable bearer token -- whose payload states the
    // person's age band, account and device fingerprint -- to disk next to
    // their real IP address, kept for fourteen days. For a site whose promise
    // is anonymity that was the whole game. The server echoes the value back
    // to complete the handshake; see server/src/socket-credential.js.
    return this._open([`lumi.t.${token}`]);
  }

  /**
   * Open a VISITOR socket: the page has no session, so there is no token. The
   * keepsake key and the fingerprint ride in the same header the token does,
   * so the server can tell this page from another and neither reaches its
   * log. It receives what the landing shows and the channels the page
   * watches, and nothing a session could do. See server/src/visitors.js.
   */
  connectVisitor({ keepsakeKey = null, fingerprint = '', accountToken = null } = {}) {
    const protocols = [keepsakeKey ? `lumi.v.${keepsakeKey}` : 'lumi.v'];
    if (fingerprint) protocols.push(`lumi.f.${fingerprint}`);
    // Signed in with no age proof stored: what the account is told must
    // still reach this page.
    if (accountToken) protocols.push(`lumi.a.${accountToken}`);
    return this._open(protocols);
  }

  _open(protocols) {
    return new Promise((resolve, reject) => {
      const gen = (this.generation += 1);
      const url = `${SERVER_WS}/ws`;
      let ws;
      try {
        ws = new WebSocket(url, protocols);
      } catch (err) {
        reject(err instanceof Error ? err : new Error('Could not connect to Lumi.'));
        return;
      }
      this.ws = ws;
      ws.onopen = () => {
        this._armWatchdog(gen);
        this._watchVisibility();
        resolve();
      };
      ws.onerror = () => reject(new Error('Could not connect to Lumi.'));
      ws.onclose = () => {
        if (gen !== this.generation) return; // already replaced; not our news
        clearInterval(this.watchdog);
        this.watchdog = null;
        if (!this.closed) this._emit('__close', {});
      };
      ws.onmessage = (ev) => {
        if (gen !== this.generation) return;
        this.lastSeen = Date.now(); // any traffic proves the server is alive
        let msg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (msg.t === ServerMsg.PING) {
          this.send(ClientMsg.PONG, {});
          return;
        }
        this._emit(msg.t, msg);
      };
    });
  }

  /** Whether frames can be sent right now. */
  isOpen() {
    return !this.closed && !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Tell the server whether this tab is the one on screen, now and whenever it
   * changes.
   *
   * The console lists everybody with the page open. A tab left open behind
   * other windows looked exactly like somebody who had just arrived and was
   * reading it, and sat at the top of the list for hours (the operator,
   * 2026-09-18). This is the one thing that tells them apart, and it says
   * nothing else: not what is on the page, not what they are doing.
   *
   * On every socket, visitor and session alike, because most of the people in
   * that list have not started anything yet. Registered once and kept for the
   * life of this Signaling; a socket that is not open sends nothing.
   */
  _watchVisibility() {
    const say = () => {
      try {
        this.send(ClientMsg.VISIBLE, { on: document.visibilityState !== 'hidden' });
      } catch {
        /* a page without a document (a test) has no tab to report */
      }
    };
    say();
    if (this._visibilityBound) return;
    this._visibilityBound = say;
    try {
      document.addEventListener('visibilitychange', say);
    } catch {
      /* nothing to listen to; the server assumes the tab is there */
    }
  }

  send(type, payload = {}) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: type, ...payload }));
    }
  }

  close() {
    this.closed = true;
    clearInterval(this.watchdog);
    this.watchdog = null;
    if (this._visibilityBound) {
      try {
        document.removeEventListener('visibilitychange', this._visibilityBound);
      } catch {
        /* never bound */
      }
      this._visibilityBound = null;
    }
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }
  }
}
